import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Html, Line, OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';

import { KILIMANJARO_ROUTES } from '../data/kilimanjaroRoutes';
import {
  BASE_RADIUS,
  MAX_HEIGHT,
  applySnowAndStreaks,
  buildMountainMesh,
  grainNoise,
  terrainColor,
} from '../three/kilimanjaroGeometry';
import { ALL_ROUTE_PATHS, CampPoint } from '../three/routePaths';

const MIN_DIST = 140;
const MAX_DIST = 430;
const ZOOM_STEP = 32;
const TARGET: [number, number, number] = [0, MAX_HEIGHT * 0.26, 0];

function campKey(cp: CampPoint): string {
  return `${cp.route.id}:${cp.camp.name}`;
}

/**
 * Builds the mountain as a real three.js BufferGeometry, colored per-vertex
 * using the same elevation-band + snow-streak + grain functions as before.
 * Lighting, depth-testing and occlusion are all handled by the GPU from here
 * on — there's no custom visibility math left to get wrong.
 */
function useMountainGeometry() {
  return useMemo(() => {
    const mesh = buildMountainMesh();
    const geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(mesh.vertices.length * 3);
    const colors = new Float32Array(mesh.vertices.length * 3);

    mesh.vertices.forEach((v, i) => {
      positions[i * 3] = v.x;
      positions[i * 3 + 1] = v.y;
      positions[i * 3 + 2] = v.z;

      const h = v.y / MAX_HEIGHT;
      const r01 = Math.min(1.4, Math.sqrt(v.x * v.x + v.z * v.z) / BASE_RADIUS);
      const rgb = applySnowAndStreaks(terrainColor(h), h, v.x, v.z, r01);
      const grain = 1 + grainNoise(v.x, v.z) * 0.05;

      colors[i * 3] = Math.min(1, (rgb[0] * grain) / 255);
      colors[i * 3 + 1] = Math.min(1, (rgb[1] * grain) / 255);
      colors[i * 3 + 2] = Math.min(1, (rgb[2] * grain) / 255);
    });

    const indices: number[] = [];
    // Winding matches the up-facing normal convention used across the project.
    mesh.faces.forEach(([a, b, c]) => indices.push(a, c, b));

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }, []);
}

function Mountain() {
  const geometry = useMountainGeometry();
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial vertexColors roughness={0.95} metalness={0} />
    </mesh>
  );
}

interface RouteLayerProps {
  selectedRouteId: string | null;
  hoveredKey: string | null;
  onHoverCamp: (key: string | null) => void;
  onCampClick: (cp: CampPoint) => void;
}

function RouteLayer({ selectedRouteId, hoveredKey, onHoverCamp, onCampClick }: RouteLayerProps) {
  return (
    <>
      {ALL_ROUTE_PATHS.map((rp) => {
        const isSelected = selectedRouteId === rp.route.id;
        const dim = selectedRouteId !== null && !isSelected;
        const points = rp.linePoints.map((p) => [p.x, p.y, p.z] as [number, number, number]);

        return (
          <group key={rp.route.id}>
            <Line points={points} color={rp.route.color} lineWidth={isSelected ? 2.6 : 1.4} transparent opacity={dim ? 0.25 : isSelected ? 1 : 0.85} />
            {rp.campPoints.map((cp) => {
              const key = campKey(cp);
              const isSummit = cp.camp.name === 'Uhuru Peak';
              const isHovered = hoveredKey === key;
              const radius = (isSummit ? 1.7 : 1.15) * (isHovered ? 1.5 : 1);
              return (
                <mesh
                  key={key}
                  position={[cp.point.x, cp.point.y, cp.point.z]}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCampClick(cp);
                  }}
                  onPointerOver={(e) => {
                    e.stopPropagation();
                    onHoverCamp(key);
                  }}
                  onPointerOut={() => onHoverCamp(null)}
                >
                  <sphereGeometry args={[radius, 16, 16]} />
                  <meshStandardMaterial
                    color={isSummit ? '#ffffff' : rp.route.color}
                    transparent
                    opacity={dim ? 0.35 : 1}
                    emissive={isSummit ? '#ffffff' : rp.route.color}
                    emissiveIntensity={isHovered ? 0.6 : 0.15}
                  />
                </mesh>
              );
            })}
          </group>
        );
      })}
    </>
  );
}

interface CameraApi {
  focusAzimuth: (theta: number) => void;
  zoomBy: (delta: number) => void;
}

/** Bridges the Canvas's camera/controls out to the DOM UI (search box, zoom buttons) that live outside the Canvas. */
function CameraBridge({ apiRef }: { apiRef: React.MutableRefObject<CameraApi | null> }) {
  const { camera } = useThree();
  const controlsRef = useRef<OrbitControlsImpl | null>(null);

  useEffect(() => {
    apiRef.current = {
      focusAzimuth: (theta: number) => {
        const controls = controlsRef.current;
        if (!controls) return;
        const target = controls.target;
        const offset = camera.position.clone().sub(target);
        const spherical = new THREE.Spherical().setFromVector3(offset);
        spherical.theta = theta;
        const newOffset = new THREE.Vector3().setFromSpherical(spherical);
        camera.position.copy(target.clone().add(newOffset));
        controls.update();
      },
      zoomBy: (delta: number) => {
        const controls = controlsRef.current;
        if (!controls) return;
        const target = controls.target;
        const offset = camera.position.clone().sub(target);
        const spherical = new THREE.Spherical().setFromVector3(offset);
        spherical.radius = Math.min(MAX_DIST, Math.max(MIN_DIST, spherical.radius + delta));
        const newOffset = new THREE.Vector3().setFromSpherical(spherical);
        camera.position.copy(target.clone().add(newOffset));
        controls.update();
      },
    };
  }, [apiRef, camera]);

  useFrame(() => {
    controlsRef.current?.update();
  });

  return (
    <OrbitControls
      ref={controlsRef}
      target={TARGET}
      autoRotate
      autoRotateSpeed={0.5}
      enableDamping
      dampingFactor={0.08}
      enablePan={false}
      minDistance={MIN_DIST}
      maxDistance={MAX_DIST}
      maxPolarAngle={Math.PI / 2 - 0.02}
      minPolarAngle={0.15}
    />
  );
}

export default function Kilimanjaro3D() {
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [activeCamp, setActiveCamp] = useState<CampPoint | null>(null);
  const [query, setQuery] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const cameraApiRef = useRef<CameraApi | null>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [] as CampPoint[];
    const out: CampPoint[] = [];
    ALL_ROUTE_PATHS.forEach((rp) => {
      rp.campPoints.forEach((cp) => {
        if (cp.camp.name.toLowerCase().includes(q) || cp.route.name.toLowerCase().includes(q)) {
          out.push(cp);
        }
      });
    });
    return out.slice(0, 8);
  }, [query]);

  const handleCampClick = useCallback((cp: CampPoint) => {
    setActiveCamp(cp);
    setSelectedRouteId(cp.route.id);
    setHasInteracted(true);
  }, []);

  const focusOnCamp = useCallback((cp: CampPoint) => {
    const angleRad = ((cp.route.angleStart + cp.t * cp.route.angleSpan) * Math.PI) / 180;
    cameraApiRef.current?.focusAzimuth(-angleRad + 0.4);
    setActiveCamp(cp);
    setSelectedRouteId(cp.route.id);
    setQuery('');
    setHasInteracted(true);
  }, []);

  return (
    <div className="kiliScene">
      <Canvas
        className="kiliCanvas"
        camera={{ position: [180, 90, 180], fov: 42, near: 1, far: 2000 }}
        onPointerMissed={() => setActiveCamp(null)}
        onPointerDown={() => setHasInteracted(true)}
      >
        <color attach="background" args={['#0a1420']} />
        <fog attach="fog" args={['#0a1420', 260, 620]} />
        <ambientLight intensity={0.55} />
        <directionalLight position={[-90, 140, -55]} intensity={1.15} />
        <hemisphereLight args={['#c7cdd2', '#0a1420', 0.4]} />

        <Mountain />
        <RouteLayer selectedRouteId={selectedRouteId} hoveredKey={hoveredKey} onHoverCamp={setHoveredKey} onCampClick={handleCampClick} />

        {activeCamp && (
          <Html position={[activeCamp.point.x, activeCamp.point.y, activeCamp.point.z]} occlude center distanceFactor={90} style={{ pointerEvents: 'none' }}>
            <div className="kiliPopup">
              <button
                type="button"
                className="kiliPopupClose"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveCamp(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
              <div className="kiliPopupRoute" style={{ color: activeCamp.route.color }}>
                {activeCamp.route.name}
              </div>
              <div className="kiliPopupName">{activeCamp.camp.name}</div>
              <div className="kiliPopupElevation">{activeCamp.camp.elevation.toLocaleString()} m</div>
              {activeCamp.camp.note && <div className="kiliPopupNote">{activeCamp.camp.note}</div>}
            </div>
          </Html>
        )}

        <CameraBridge apiRef={cameraApiRef} />
      </Canvas>

      {!hasInteracted && <div className="kiliHint">Drag to rotate · Click a camp for details</div>}

      <div className="kiliSearch">
        <input
          type="text"
          value={query}
          placeholder="Search a route or camp…"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setHasInteracted(true)}
        />
        {matches.length > 0 && (
          <div className="kiliSearchResults">
            {matches.map((m) => (
              <button key={campKey(m)} type="button" onClick={() => focusOnCamp(m)}>
                <span className="kiliDot" style={{ background: m.route.color }} />
                {m.camp.name}
                <span className="kiliSearchMeta">
                  {m.route.name} · {m.camp.elevation.toLocaleString()} m
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="kiliZoom">
        <button type="button" aria-label="Zoom in" onClick={() => cameraApiRef.current?.zoomBy(-ZOOM_STEP)}>
          +
        </button>
        <button type="button" aria-label="Zoom out" onClick={() => cameraApiRef.current?.zoomBy(ZOOM_STEP)}>
          −
        </button>
      </div>

      <div className="kiliLegend">
        <button
          type="button"
          className={selectedRouteId === null ? 'kiliChip kiliChipActive' : 'kiliChip'}
          onClick={() => {
            setSelectedRouteId(null);
            setHasInteracted(true);
          }}
        >
          All routes
        </button>
        {KILIMANJARO_ROUTES.map((route) => (
          <button
            key={route.id}
            type="button"
            className={selectedRouteId === route.id ? 'kiliChip kiliChipActive' : 'kiliChip'}
            onClick={() => {
              setSelectedRouteId(selectedRouteId === route.id ? null : route.id);
              setHasInteracted(true);
            }}
          >
            <span className="kiliDot" style={{ background: route.color }} />
            {route.name.replace(' Route', '')}
          </button>
        ))}
      </div>
    </div>
  );
}
